import { Descriptions, Button, Space, Popover, Modal } from 'antd';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  getConfigs,
  getSubsidiaries,
  updateCommissions,
} from '../../../../appRedux/actions';
import { EditOutlined, ReloadOutlined } from '@ant-design/icons';
import { ModalView } from '../../../uiComponents';
import { useCustomModal } from '../../../../hooks/useCustomModal';
import { SettingPayementProcessModal } from '../SettingPayementProcessModal';
import { SettingsExchangeRateModal } from '../SettingsExchangeRateModal';
import {
  buttonLabel,
  settingLabel,
  settingOptions,
  settingsConst,
  updateCommisionsModal,
} from '../../../../constants';
import { SettingsShowExchangeRateModal } from '../SettingsShowExchangeRateModal';
import { SettingComissionRateModal } from '../SettingComissionRateModal';
import { SettingStrikerAppointmentModal } from '../SettingStrikerAppointmentModal';
import { SettingsAbscenceTimeModalForm } from '../SettingsAbscenceTimeModalForm';
import { SettingsShowAbscenceTimeModal } from '../SettingsAbscenceTimePopover';

export const SettingsTable = () => {
  const dispatch = useDispatch();
  const { configs } = useSelector((state) => state.configs);
  const { modal } = useCustomModal();
  const [modalTitle, setModalTitle] = useState(null);
  const [children, setChildren] = useState(null);
  const [width, setWidth] = useState(900);

  const settingsTranslations = useMemo(
    () => ({
      exchangeRate: 'Tipo de cambio',
      paymentProcessType: 'Procesador de pago',
      comissionRate: 'Porcentaje de comisión',
      strikeAppointment: 'Limite de cancelación de citas (strikes)',
      abscenceTime: 'Tiempo de inactividad',
    }),
    []
  );

  const handleUpdateCommissions = () => {
    Modal.confirm({
      title: updateCommisionsModal.title,
      content: updateCommisionsModal.body,
      okText: buttonLabel.update,
      cancelText: buttonLabel.cancel,
      okType: 'primary',
      onOk() {
        dispatch(updateCommissions());
      },
    });
  };

  const excludes = useMemo(() => ['id', 'created_at'], []);

  useEffect(() => {
    dispatch(getConfigs());
    dispatch(getSubsidiaries());
  }, [dispatch]);

  const prefix = useMemo(
    () => ({
      exchangeRate: '$ ',
      paymentProcessType: '',
      comissionRate: '',
    }),
    []
  );

  const getModal = useCallback(
    (action = null) => {
      modal();
      switch (action) {
        case 'exchangeRate':
          setModalTitle(settingsConst.exchangeRate);
          setWidth(900);
          setChildren(
            <SettingsExchangeRateModal
              onSuccess={() => dispatch(getConfigs())}
            />
          );
          break;
        case 'paymentProcessType':
          setModalTitle(settingsConst.paymentProcessType);
          setWidth(900);
          setChildren(
            <SettingPayementProcessModal
              onSuccess={() => dispatch(getConfigs())}
            />
          );
          break;
        case 'comissionRate':
          setModalTitle(settingsConst.comissionRate);
          setWidth(1200);
          setChildren(
            <SettingComissionRateModal
              onSuccess={() => dispatch(getConfigs())}
            />
          );
          break;
        case 'strikeAppointment':
          setModalTitle(settingsConst.strikeAppointment);
          setWidth(1100);
          setChildren(
            <SettingStrikerAppointmentModal
              onSuccess={() => dispatch(getConfigs())}
            />
          );
          break;
        case 'abscenceTime':
          setModalTitle(settingsConst.abscenceTime);
          setWidth(1300);
          setChildren(
            <SettingsAbscenceTimeModalForm
              onSuccess={() => dispatch(getConfigs())}
            />
          );
          break;
        default:
          break;
      }
    },
    [modal, dispatch]
  );

  const entries = useMemo(() => Object.entries(configs || {}), [configs]);

  const renderConfigValue = useCallback(
    (key, value) => {
      if (key === settingOptions.exchangeRate) {
        return (
          <Popover
            placement="rightBottom"
            content={<SettingsShowExchangeRateModal />}
            trigger="hover"
          >
            <Button type="link">{settingLabel.exchangeRates}</Button>
          </Popover>
        );
      }

      if (key === settingOptions.comissionRate) {
        const content =
          value && Array.isArray(value) && value.length ? (
            <div style={{ maxWidth: 300 }}>
              {value.map((item, idx) => (
                <div key={idx} style={{ marginBottom: 8 }}>
                  {`${item.percentage ?? '-'}% (Min: ${item.min ?? '-'}, Max: ${
                    item.max ?? '-'
                  })`}
                </div>
              ))}
            </div>
          ) : (
            <div>-</div>
          );

        return (
          <Popover placement="rightBottom" content={content} trigger="hover">
            <Button type="link">Ver configuración</Button>
          </Popover>
        );
      }

      if (key === settingOptions.abscenceTime) {
        return (
          <Popover
            placement="rightBottom"
            content={<SettingsShowAbscenceTimeModal />}
            trigger="hover"
          >
            <Button type="link">{settingLabel.abscenceTime}</Button>
          </Popover>
        );
      }

      return `${prefix[key] ?? ''}${value ?? '-'}`;
    },
    [prefix]
  );

  return (
    <>
      <Descriptions bordered column={3}>
        <Descriptions.Item
          label={settingLabel.setting}
          style={{ width: '30%' }}
        >
          {settingLabel.value}
        </Descriptions.Item>
        <Descriptions.Item style={{ width: '20%' }}>
          {settingLabel.options}
        </Descriptions.Item>
      </Descriptions>

      {entries.map(([key, value]) =>
        !excludes.includes(key) ? (
          <Descriptions key={key} bordered column={3}>
            <Descriptions.Item
              label={settingsTranslations[key] || key}
              style={{ width: '30%' }}
            >
              {renderConfigValue(key, value)}
            </Descriptions.Item>

            <Descriptions.Item style={{ width: '20%' }}>
              <Space>
                <Button
                  icon={<EditOutlined />}
                  size="small"
                  onClick={() => getModal(key)}
                />

                {key === settingOptions.comissionRate && (
                  <Button
                    icon={<ReloadOutlined />}
                    size="small"
                    onClick={handleUpdateCommissions}
                  />
                )}
              </Space>
            </Descriptions.Item>
          </Descriptions>
        ) : null
      )}
      <ModalView title={modalTitle} width={width}>
        {children}
      </ModalView>
    </>
  );
};
